import type { TranslationProvider } from '@ports/TranslationProvider.ts';
import {
  isV2MemberOnly,
  isV2QuotaError,
  type V2Body,
} from '../net/v2Client.ts';
import {
  messageForTranslationFailure,
  TranslationError,
} from './translationFailure.ts';

export type MemberTranslationEngine = 'azure' | 'deepl' | 'ai';

export type MemberTranslationProviderOptions = {
  engine: MemberTranslationEngine;
  userId: string;
  userToken: string;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
};

type MemberTranslateOk = {
  result?: Array<{ translations?: Array<{ text?: string }> }>;
  content?: string;
};

/**
 * Patreon-member engines: azure / deepl / ai.
 * Body matches extension translationLegacyBridge: { id, token, to, language, standard, subs }.
 */
export class MemberTranslationProvider implements TranslationProvider {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly engine: MemberTranslationEngine;
  private readonly userId: string;
  private readonly userToken: string;

  constructor(options: MemberTranslationProviderOptions) {
    this.engine = options.engine;
    this.userId = options.userId;
    this.userToken = options.userToken;
    this.baseUrl = (options.baseUrl ?? 'https://netflixsubs.app').replace(/\/$/, '');
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  async translate(texts: string[], targetLanguage: string): Promise<string[]> {
    if (texts.length === 0) return [];
    const path =
      this.engine === 'ai' ? '/v2/subtitles/translate' : `/v2/translate/${this.engine}`;
    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: this.userId,
          token: this.userToken,
          to: targetLanguage,
          language: targetLanguage,
          standard: true,
          subs: texts.map((text) => ({ text })),
        }),
      });
    } catch {
      throw new TranslationError('failed', `Member translate (${this.engine}) network error`);
    }

    let json: V2Body<MemberTranslateOk>;
    try {
      json = (await res.json()) as V2Body<MemberTranslateOk>;
    } catch {
      throw new TranslationError('failed', `Member translate (${this.engine}) invalid JSON`);
    }

    if (isV2QuotaError(json)) {
      throw new TranslationError('quota');
    }
    if (isV2MemberOnly(json)) {
      throw new TranslationError('member_only');
    }
    if (!res.ok || !json || json.ok !== true || !json.data?.result) {
      throw new TranslationError(
        'failed',
        `Member translate (${this.engine}) failed: ${res.status}`,
      );
    }
    return texts.map(
      (_, i) =>
        json.data?.result?.[i]?.translations?.[0]?.text
        ?? messageForTranslationFailure('failed'),
    );
  }
}
